import type { Project } from "./types";
import type { DesktopBridge, DesktopOpenLocalRequest } from "@cmmd-forge/contracts";
import {
  resolveOpenLocalTarget,
  type OpenLocalCandidate,
  type OpenLocalMatch,
  type OpenLocalTarget,
} from "@cmmd-forge/shared/openLocalTarget";

/**
 * Feeds the renderer's project list into the `Open locally` resolver.
 *
 * The resolver itself is repo-agnostic and takes remote URLs. This is the
 * adapter, and it is the one place a silent mapping bug could live — pointing
 * `workspaceRoot` at the wrong field would check a branch out in the wrong
 * directory without anything failing.
 *
 * Two projects are deliberately NOT candidates:
 *
 * - No `cwd`. There is nowhere on disk to check anything out, so it cannot
 *   satisfy the link no matter which repo it claims.
 * - No `repositoryIdentity`. Identity is derived from the git remote, so a
 *   project without one has no remote to compare against. It is dropped here
 *   as `null` rather than guessed at from the folder name — a directory called
 *   `forge` is not evidence that it IS `owner/forge`.
 */

export type OpenLocalProject = Pick<Project, "id" | "cwd" | "repositoryIdentity" | "environmentId">;

export function buildOpenLocalCandidates(
  projects: readonly OpenLocalProject[],
): readonly OpenLocalCandidate[] {
  const candidates: OpenLocalCandidate[] = [];
  for (const project of projects) {
    const workspaceRoot = project.cwd?.trim();
    if (!workspaceRoot) continue;
    candidates.push({
      projectId: String(project.id),
      workspaceRoot,
      remoteUrl: project.repositoryIdentity?.locator.remoteUrl ?? null,
    });
  }
  return candidates;
}

export function resolveOpenLocalHandoff(
  repoFullName: string,
  projects: readonly OpenLocalProject[],
): OpenLocalTarget {
  return resolveOpenLocalTarget(repoFullName, buildOpenLocalCandidates(projects));
}

/**
 * What the renderer should do about a pending `Open locally` request.
 *
 * Each variant carries the original request so the UI can name the repo and
 * branch the user clicked — "no project for CMMD-Center/forge" is actionable,
 * "no project found" is not.
 */
export type OpenLocalHandoffOutcome =
  | {
      readonly kind: "open";
      readonly request: DesktopOpenLocalRequest;
      readonly match: OpenLocalMatch;
    }
  | {
      readonly kind: "ambiguous";
      readonly request: DesktopOpenLocalRequest;
      readonly candidates: readonly OpenLocalMatch[];
    }
  | { readonly kind: "no-match"; readonly request: DesktopOpenLocalRequest };

/**
 * Take the pending request, if any, and decide what to do with it.
 *
 * Returns `null` for the overwhelmingly common case — an app launch nobody
 * triggered with a link — and for every environment that cannot carry one: the
 * browser build with no bridge at all, and desktop builds predating the
 * `takePendingOpenLocalRequest` method.
 *
 * A bridge that THROWS is reported through `onError` and treated as "nothing
 * pending". This runs during startup, and a deep link failing is not a reason
 * for the app to fail to start — but the swallow is deliberate and observable
 * rather than a bare try/catch that loses the error.
 */
export async function takeOpenLocalRequest(
  bridge: Pick<DesktopBridge, "takePendingOpenLocalRequest"> | null | undefined,
  options: { readonly onError?: (error: unknown) => void } = {},
): Promise<DesktopOpenLocalRequest | null> {
  if (!bridge?.takePendingOpenLocalRequest) return null;
  try {
    return await bridge.takePendingOpenLocalRequest();
  } catch (error) {
    options.onError?.(error);
    return null;
  }
}

/**
 * Decide what to show for a request already taken.
 *
 * ## Why an empty project list yields `null` rather than "no match"
 *
 * Taking a request CLEARS it in the main process, so it can only be taken once
 * — but the project list is derived from the store and is empty both while it
 * is still loading and when the user genuinely has no projects. Resolving
 * against an empty list at mount would answer "no local copy of that repo"
 * before the answer is knowable, and the request would already be gone.
 *
 * So taking and resolving are separate: the request is captured once and held,
 * and this runs again as the list settles. Until at least one project exists
 * there is no answer worth showing, and a momentary wrong "no local copy" that
 * later flips to a match is worse than a slightly later dialog.
 */
export function resolvePendingHandoff(
  request: DesktopOpenLocalRequest | null,
  projects: readonly OpenLocalProject[],
): OpenLocalHandoffOutcome | null {
  if (!request || projects.length === 0) return null;

  const target = resolveOpenLocalHandoff(request.repoFullName, projects);
  if (target.ok) return { kind: "open", request, match: target.match };
  if (target.reason === "ambiguous-match") {
    return { kind: "ambiguous", request, candidates: target.candidates };
  }
  return { kind: "no-match", request };
}

/**
 * Take and resolve in one step. Safe only where the project list is already
 * known to be settled; the coordinator uses the two halves separately.
 */
export async function takeOpenLocalHandoff(
  bridge: Pick<DesktopBridge, "takePendingOpenLocalRequest"> | null | undefined,
  projects: readonly OpenLocalProject[],
  options: { readonly onError?: (error: unknown) => void } = {},
): Promise<OpenLocalHandoffOutcome | null> {
  const request = await takeOpenLocalRequest(bridge, options);
  if (!request) return null;

  const target = resolveOpenLocalHandoff(request.repoFullName, projects);
  if (target.ok) return { kind: "open", request, match: target.match };
  if (target.reason === "ambiguous-match") {
    return { kind: "ambiguous", request, candidates: target.candidates };
  }
  return { kind: "no-match", request };
}
